import React, { useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { useNav, VIEWS } from "@/lib/nav";

const PREVIEW = 8;
const cache = {};

/** "on N cards" badge: hover previews the models with this rule, click pins the full clickable list. */
export function RuleCardsBadge({ ruleId, count }) {
  const { navigate } = useNav();
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [cards, setCards] = useState(cache[ruleId] || null);
  const hideTimer = useRef(null);

  const load = () => {
    if (cards) return;
    api.getRule(ruleId).then((r) => { cache[ruleId] = r.cards || []; setCards(cache[ruleId]); }).catch(() => setCards([]));
  };
  const show = () => { clearTimeout(hideTimer.current); load(); setOpen(true); };
  const hide = () => { if (!pinned) hideTimer.current = setTimeout(() => setOpen(false), 150); };
  const toggle = (e) => {
    e.stopPropagation();
    load();
    if (pinned) { setPinned(false); setOpen(false); } else { setPinned(true); setOpen(true); }
  };
  const list = pinned ? cards : cards?.slice(0, PREVIEW);

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setPinned(false); }}>
      <PopoverAnchor asChild>
        <span onMouseEnter={show} onMouseLeave={hide} onClick={toggle} role="button" tabIndex={0} data-testid={`rule-cards-badge-${ruleId}`}>
          <Badge variant="outline" className="text-muted-foreground cursor-pointer hover:text-foreground hover:border-cyan-600/60">
            on {count} card{count === 1 ? "" : "s"}
          </Badge>
        </span>
      </PopoverAnchor>
      <PopoverContent align="start" className="w-80 p-3" onMouseEnter={show} onMouseLeave={hide} onClick={(e) => e.stopPropagation()} onOpenAutoFocus={(e) => e.preventDefault()} data-testid={`rule-cards-popover-${ruleId}`}>
        <p className="text-xs text-muted-foreground mb-2">{pinned ? `All ${count} models with this rule` : "Models with this rule"}</p>
        {!cards ? (
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        ) : (
          <ul className={`space-y-0.5 ${pinned ? "max-h-72 overflow-y-auto pr-1" : ""}`}>
            {list.map((c) => (
              <li key={c.id}>
                <button type="button" onClick={() => { setOpen(false); navigate(VIEWS.card(c.id)); }} className="w-full text-left text-sm rounded px-1.5 py-0.5 hover:bg-card flex justify-between gap-2" data-testid={`rule-cards-item-${c.id}`}>
                  <span className="truncate">{c.name}</span>
                  <span className="text-[11px] text-muted-foreground shrink-0">{c.faction}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {!pinned && cards && cards.length > PREVIEW && (
          <p className="text-[11px] text-cyan-400 mt-2">+{cards.length - PREVIEW} more — click the badge to see all</p>
        )}
      </PopoverContent>
    </Popover>
  );
}
